import type { DistrictCanvasSection } from './advisor'

export const DISTRICT_MIRROR_PANEL = 'district' as const
export const DISTRICT_MIRROR_SOURCE = 'district_synthetic' as const
export const DISTRICT_MIRROR_FUTURE_SOURCE = 'district_core' as const
export const DISTRICT_CONNECTOR_FOUNDATION_SNAPSHOT = 'nexus.district.connector-foundation.snapshot.v1' as const

const SECTIONS: readonly DistrictCanvasSection[] = [
  'overview', 'schools', 'school-overview', 'enrollment', 'exceptions', 'transfers',
  'staff', 'connectors', 'capacity', 'health', 'audit', 'policies',
]

export type DistrictConnectorFoundationSnapshot = Readonly<{
  schema_version: typeof DISTRICT_CONNECTOR_FOUNDATION_SNAPSHOT
  read_only: true
  source: typeof DISTRICT_MIRROR_SOURCE
  future_provider: typeof DISTRICT_MIRROR_FUTURE_SOURCE
  connector_catalog: ReadonlyArray<Readonly<{
    id: string
    name: string
    pipeline: 'student_sis' | 'employee_hris'
    adapter: string
    adapter_version: string
    state: 'demo_healthy' | 'contract_ready' | 'not_configured' | 'future_adapter'
    status_label: 'Demo healthy' | 'Contract ready' | 'Not configured' | 'Future adapter'
    credential_posture: 'external_secret_reference_only' | 'not_configured'
  }>>
  sync_jobs: ReadonlyArray<Readonly<{
    id: string
    connector_id: string
    pipeline: 'student_sis' | 'employee_hris'
    state: string
    cursor: string
    correlation_id: string
    idempotency_key: string
    counts: Readonly<{ staged: number; validated: number; provisional: number; quarantined: number }>
  }>>
  diff_previews: ReadonlyArray<Readonly<{
    job_id: string
    idempotency_key: string
    read_only: true
    changes: ReadonlyArray<Readonly<{ record_ref: string; semantic: 'create' | 'update' | 'deactivate' | 'noop' | 'conflict'; reason_code: string }>>
    summary: Readonly<{ create: number; update: number; deactivate: number; noop: number; conflict: number }>
    next_action: 'blocked_by_authorization'
  }>>
  exceptions: ReadonlyArray<Readonly<{
    id: string
    job_id: string
    pipeline: 'student_sis' | 'employee_hris'
    state: 'district_provisional' | 'quarantined'
    reason_code: string
    record_ref: string
    sensitive_payload_available: false
    resolution: 'manual_review_required'
  }>>
  audit_events: ReadonlyArray<Readonly<{
    event_id: string
    action: string
    result: 'allowed' | 'blocked' | 'recorded'
    occurred_at: string
    correlation_id: string
    classification: 'metadata_only'
  }>>
}>

export type DistrictMirrorSnapshot = Readonly<{
  schema_version: 'nexus.district.mirror.snapshot.v1'
  provider: typeof DISTRICT_MIRROR_SOURCE
  future_provider: typeof DISTRICT_MIRROR_FUTURE_SOURCE
  capability: 'mirror.panel.district_demo'
  activation?: 'server_capability' | 'local_synthetic_demo'
  visual_actor: 'District Administrator Demo'
  visual_role: 'district_admin_demo'
  synthetic_visual_context: true
  read_only: true
  section: DistrictCanvasSection
  schools: ReadonlyArray<Readonly<{
    org_school_id: 'org-school-mode-demo'
    label: 'School Mode Demonstration School'
    campus_count: number
    classroom_count: number
  }>>
  advisor: Readonly<{
    mode: 'district_synthetic_read_only'
    can_mutate: false
    can_grant_permissions: false
    can_connect_services: false
  }>
  connector_foundation: DistrictConnectorFoundationSnapshot
}>

export type DistrictMirrorState = Readonly<{
  revision: number
  active_panel: typeof DISTRICT_MIRROR_PANEL
  view: Readonly<{
    kind: 'district'
    source: typeof DISTRICT_MIRROR_SOURCE
    read_only: true
    editable: false
    shareable: false
    downloadable: false
    extra: Readonly<{ district_snapshot: DistrictMirrorSnapshot }>
  }>
}>

const object = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object' && !Array.isArray(value)
const safeText = (value: unknown): value is string => typeof value === 'string' && value.length > 0 && value.length <= 160 && !/[@\x00-\x1f]|https?:\/\//i.test(value)
const identifier = (value: unknown): value is string => safeText(value) && /^[a-zA-Z0-9][a-zA-Z0-9._:-]*$/.test(value)
const count = (value: unknown): value is number => Number.isSafeInteger(value) && Number(value) >= 0 && Number(value) <= 1_000_000
const keys = (value: Record<string, unknown>, allowed: readonly string[]) => Object.keys(value).every((key) => allowed.includes(key))
const list = (value: unknown): value is Record<string, unknown>[] => Array.isArray(value) && value.length <= 1000 && value.every(object)
const unique = (items: Record<string, unknown>[], key: string) => new Set(items.map((item) => item[key])).size === items.length
const pipelines = ['student_sis', 'employee_hris']
const semantics = ['create', 'update', 'deactivate', 'noop', 'conflict']
const jobStates = ['configured', 'fetching', 'staged', 'validated', 'preview_ready', 'awaiting_approval', 'approved', 'applying', 'reconciled', 'failed', 'quarantined', 'cancelled']
const reasonCodes = ['UNKNOWN_SCHOOL_DESTINATION', 'MISSING_SCHOOL_DESTINATION', 'STAFF_RECORD_IN_STUDENT_PIPELINE', 'CROSS_TENANT_DENIED', 'SOURCE_VERSION_CONFLICT']

/** Structural validation is never proof of an actor capability or a lease. */
function validSnapshotContents(snapshot: Record<string, unknown>): boolean {
  if (!keys(snapshot, ['schema_version', 'provider', 'future_provider', 'capability', 'activation', 'visual_actor', 'visual_role', 'synthetic_visual_context', 'read_only', 'section', 'schools', 'advisor', 'connector_foundation'])) return false
  if (!list(snapshot.schools) || snapshot.schools.length !== 1) return false
  const school = snapshot.schools[0]
  if (!school || !keys(school, ['org_school_id', 'label', 'campus_count', 'classroom_count'])
    || school.org_school_id !== 'org-school-mode-demo' || school.label !== 'School Mode Demonstration School'
    || !count(school.campus_count) || Number(school.campus_count) > 100
    || !count(school.classroom_count) || Number(school.classroom_count) > 1000) return false
  const advisor = snapshot.advisor
  if (!object(advisor) || !keys(advisor, ['mode', 'can_mutate', 'can_grant_permissions', 'can_connect_services'])) return false
  const foundation = snapshot.connector_foundation
  if (!object(foundation) || !keys(foundation, ['schema_version', 'read_only', 'source', 'future_provider', 'connector_catalog', 'sync_jobs', 'diff_previews', 'exceptions', 'audit_events'])) return false
  const { connector_catalog: catalog, sync_jobs: jobs, diff_previews: previews, exceptions, audit_events: events } = foundation
  if (!list(catalog) || catalog.length < 2 || catalog.length > 20 || !unique(catalog, 'id')
    || !list(jobs) || !unique(jobs, 'id') || !list(previews) || !unique(previews, 'job_id')
    || !list(exceptions) || !unique(exceptions, 'id') || !list(events) || !unique(events, 'event_id')) return false
  if (!catalog.every((item) => keys(item, ['id', 'name', 'pipeline', 'adapter', 'adapter_version', 'state', 'status_label', 'credential_posture'])
    && identifier(item.id) && safeText(item.name) && pipelines.includes(String(item.pipeline))
    && ['synthetic_fixture', 'oneroster_csv', 'oneroster_rest', 'edfi', 'sftp_csv', 'custom_rest', 'hris_staff'].includes(String(item.adapter))
    && safeText(item.adapter_version)
    && ({ demo_healthy: 'Demo healthy', contract_ready: 'Contract ready', not_configured: 'Not configured', future_adapter: 'Future adapter' } as Record<string, string>)[String(item.state)] === item.status_label
    && (item.pipeline !== 'employee_hris' || (item.adapter === 'hris_staff' && item.state === 'not_configured')))) return false
  const healthy = catalog.filter((item) => item.state === 'demo_healthy')
  const healthyConnector = healthy[0]
  if (healthy.length !== 1 || !healthyConnector || healthyConnector.id !== 'oneroster-csv-synthetic' || healthyConnector.adapter !== 'oneroster_csv' || healthyConnector.pipeline !== 'student_sis') return false
  if (!jobs.every((item) => keys(item, ['id', 'connector_id', 'pipeline', 'state', 'cursor', 'correlation_id', 'idempotency_key', 'counts'])
    && ['id', 'connector_id', 'cursor', 'correlation_id', 'idempotency_key'].every((key) => identifier(item[key]))
    && jobStates.includes(String(item.state)) && catalog.some((connector) => connector.id === item.connector_id && connector.pipeline === item.pipeline)
    && object(item.counts) && keys(item.counts, ['staged', 'validated', 'provisional', 'quarantined'])
    && ['staged', 'validated', 'provisional', 'quarantined'].every((key) => count((item.counts as Record<string, unknown>)[key])))) return false
  if (!previews.every((item) => {
    const summary = item.summary
    const changes = item.changes
    if (!keys(item, ['job_id', 'idempotency_key', 'read_only', 'changes', 'summary', 'next_action'])
      || !jobs.some((job) => job.id === item.job_id && job.idempotency_key === item.idempotency_key)
      || !object(summary) || !keys(summary, semantics) || !semantics.every((key) => count(summary[key])) || !list(changes)) return false
    return changes.every((change) => keys(change, ['record_ref', 'semantic', 'reason_code']) && identifier(change.record_ref) && semantics.includes(String(change.semantic)) && identifier(change.reason_code))
      && semantics.every((key) => summary[key] === changes.filter((change) => change.semantic === key).length)
  })) return false
  if (!exceptions.every((item) => keys(item, ['id', 'job_id', 'pipeline', 'state', 'reason_code', 'record_ref', 'sensitive_payload_available', 'resolution'])
    && identifier(item.id) && identifier(item.record_ref)
    && jobs.some((job) => job.id === item.job_id && job.pipeline === item.pipeline)
    && ['district_provisional', 'quarantined'].includes(String(item.state)) && reasonCodes.includes(String(item.reason_code)))) return false
  return events.every((item) => keys(item, ['event_id', 'action', 'result', 'occurred_at', 'correlation_id', 'classification'])
    && identifier(item.event_id) && identifier(item.action) && identifier(item.correlation_id)
    && typeof item.occurred_at === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(item.occurred_at) && Number.isFinite(Date.parse(item.occurred_at)))
}

export function isDistrictMirrorState(value: unknown): value is DistrictMirrorState {
  if (!value || typeof value !== 'object') return false
  const state = value as Record<string, unknown>
  const view = state.view as Record<string, unknown> | undefined
  const extra = view?.extra as Record<string, unknown> | undefined
  const snapshot = extra?.district_snapshot as Record<string, unknown> | undefined
  if (!object(snapshot) || !validSnapshotContents(snapshot)) return false
  const foundation = snapshot?.connector_foundation as Record<string, unknown> | undefined
  const catalog = foundation?.connector_catalog
  const jobs = foundation?.sync_jobs
  const previews = foundation?.diff_previews
  const exceptions = foundation?.exceptions
  const auditEvents = foundation?.audit_events
  return Number.isInteger(state.revision)
    && Number(state.revision) > 0
    && state.active_panel === DISTRICT_MIRROR_PANEL
    && view?.kind === 'district'
    && view?.source === DISTRICT_MIRROR_SOURCE
    && view?.read_only === true
    && view?.editable === false
    && view?.shareable === false
    && view?.downloadable === false
    && snapshot?.schema_version === 'nexus.district.mirror.snapshot.v1'
    && snapshot?.provider === DISTRICT_MIRROR_SOURCE
    && snapshot?.future_provider === DISTRICT_MIRROR_FUTURE_SOURCE
    && snapshot?.capability === 'mirror.panel.district_demo'
    && (snapshot?.activation === undefined || snapshot.activation === 'server_capability' || snapshot.activation === 'local_synthetic_demo')
    && snapshot?.visual_actor === 'District Administrator Demo'
    && snapshot?.visual_role === 'district_admin_demo'
    && snapshot?.synthetic_visual_context === true
    && snapshot?.read_only === true
    && typeof snapshot?.section === 'string'
    && SECTIONS.includes(snapshot.section as DistrictCanvasSection)
    && Array.isArray(snapshot?.schools)
    && snapshot.schools.length === 1
    && (snapshot.advisor as Record<string, unknown> | undefined)?.mode === 'district_synthetic_read_only'
    && (snapshot.advisor as Record<string, unknown> | undefined)?.can_mutate === false
    && (snapshot.advisor as Record<string, unknown> | undefined)?.can_grant_permissions === false
    && (snapshot.advisor as Record<string, unknown> | undefined)?.can_connect_services === false
    && foundation?.schema_version === DISTRICT_CONNECTOR_FOUNDATION_SNAPSHOT
    && foundation?.read_only === true
    && foundation?.source === DISTRICT_MIRROR_SOURCE
    && foundation?.future_provider === DISTRICT_MIRROR_FUTURE_SOURCE
    && Array.isArray(catalog)
    && catalog.length >= 2
    && catalog.every((connector) => {
      const value = connector as Record<string, unknown>
      return typeof value.id === 'string'
        && typeof value.name === 'string'
        && (value.pipeline === 'student_sis' || value.pipeline === 'employee_hris')
        && typeof value.adapter === 'string'
        && typeof value.adapter_version === 'string'
        && ['demo_healthy', 'contract_ready', 'not_configured', 'future_adapter'].includes(String(value.state))
        && ['Demo healthy', 'Contract ready', 'Not configured', 'Future adapter'].includes(String(value.status_label))
        && ['external_secret_reference_only', 'not_configured'].includes(String(value.credential_posture))
    })
    && catalog.filter((connector) => (connector as Record<string, unknown>).state === 'demo_healthy').length === 1
    && catalog.some((connector) => (connector as Record<string, unknown>).pipeline === 'employee_hris')
    && Array.isArray(jobs)
    && jobs.every((job) => {
      const value = job as Record<string, unknown>
      const counts = value.counts as Record<string, unknown> | undefined
      return typeof value.id === 'string'
        && typeof value.connector_id === 'string'
        && (value.pipeline === 'student_sis' || value.pipeline === 'employee_hris')
        && typeof value.state === 'string'
        && typeof value.cursor === 'string'
        && typeof value.correlation_id === 'string'
        && typeof value.idempotency_key === 'string'
        && ['staged', 'validated', 'provisional', 'quarantined'].every((key) => Number.isInteger(counts?.[key]) && Number(counts?.[key]) >= 0)
    })
    && Array.isArray(previews)
    && previews.every((preview) => {
      const value = preview as Record<string, unknown>
      return typeof value.job_id === 'string'
        && typeof value.idempotency_key === 'string'
        && value.read_only === true
        && value.next_action === 'blocked_by_authorization'
        && Array.isArray(value.changes)
    })
    && Array.isArray(exceptions)
    && exceptions.every((exception) => {
      const value = exception as Record<string, unknown>
      return typeof value.id === 'string'
        && typeof value.job_id === 'string'
        && value.sensitive_payload_available === false
        && value.resolution === 'manual_review_required'
    })
    && Array.isArray(auditEvents)
    && auditEvents.every((event) => {
      const value = event as Record<string, unknown>
      return typeof value.event_id === 'string'
        && typeof value.action === 'string'
        && ['allowed', 'blocked', 'recorded'].includes(String(value.result))
        && value.classification === 'metadata_only'
    })
}

export function districtSnapshotFromMirrorState(state: DistrictMirrorState): DistrictMirrorSnapshot {
  return state.view.extra.district_snapshot
}
